import React, { useState, useEffect } from 'react';
import { 
  Building2, Save, Plus, Trash2, Edit2, CheckCircle2, 
  AlertCircle, MapPin, FileText, Shield, Info, Globe,
  Hash, Calendar, DollarSign
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Alert, AlertDescription } from '../../ui/alert';
import { db } from '../../../lib/db';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { STATE_TAX_DATA, getStatesForDropdown } from '../../../lib/usPayrollCalculations';
import { SUI_RATES, TAX_DEADLINES, TAX_FORMS } from '../../../lib/usPayrollData';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

const US_STATES = getStatesForDropdown();

const TAX_FILING_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiweekly', label: 'Semi-Weekly' },
  { value: 'annual', label: 'Annual' }
];

export default function CompanyTaxSettings() {
  const { confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('federal');
  
  // Company Settings
  const [companySettings, setCompanySettings] = useState({
    id: '1',
    ein: '',
    companyName: '',
    legalName: '',
    dba: '',
    entityType: 'llc',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'US'
    },
    mailingAddress: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'US'
    },
    sameAsPhysical: true,
    phone: '',
    email: '',
    fiscalYearEnd: '12'
  });

  // Tax Accounts
  const [taxAccounts, setTaxAccounts] = useState([]);
  const [showTaxAccountModal, setShowTaxAccountModal] = useState(false);
  const [editingTaxAccount, setEditingTaxAccount] = useState(null);
  const [newTaxAccount, setNewTaxAccount] = useState({
    jurisdiction: 'federal',
    taxType: 'withholding',
    state: '',
    accountNumber: '',
    filingFrequency: 'quarterly',
    depositSchedule: 'monthly',
    status: 'active'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settings, accounts] = await Promise.all([
        db.companySettings.get('1'),
        db.taxAccounts.toArray()
      ]);
      
      if (settings) {
        setCompanySettings(settings);
      }
      setTaxAccounts(accounts || []);
    } catch (error) {
      console.error('Error loading tax settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanySettings = async () => {
    setSaving(true);
    try {
      await db.companySettings.put({
        ...companySettings,
        updatedAt: Date.now()
      });
      toast.success('Company settings saved successfully');
    } catch (error) {
      console.error('Error saving company settings:', error);
      toast.error('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTaxAccount = async () => {
    try {
      const account = {
        id: uuidv4(),
        ...newTaxAccount,
        createdAt: Date.now()
      };
      await db.taxAccounts.add(account);
      setTaxAccounts([...taxAccounts, account]);
      setShowTaxAccountModal(false);
      setNewTaxAccount({
        jurisdiction: 'federal',
        taxType: 'withholding',
        state: '',
        accountNumber: '',
        filingFrequency: 'quarterly',
        depositSchedule: 'monthly',
        status: 'active'
      });
      toast.success('Tax account added successfully');
    } catch (error) {
      toast.error('Failed to add tax account');
    }
  };

  const handleUpdateTaxAccount = async () => {
    try {
      await db.taxAccounts.put({
        ...editingTaxAccount,
        updatedAt: Date.now()
      });
      setTaxAccounts(taxAccounts.map(a => 
        a.id === editingTaxAccount.id ? editingTaxAccount : a
      ));
      setEditingTaxAccount(null);
      toast.success('Tax account updated successfully');
    } catch (error) {
      toast.error('Failed to update tax account');
    }
  };

  const handleDeleteTaxAccount = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Tax Account',
      description: 'Are you sure you want to delete this tax account?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      await db.taxAccounts.delete(id);
      setTaxAccounts(taxAccounts.filter(a => a.id !== id));
      toast.success('Tax account deleted');
    } catch (error) {
      toast.error('Failed to delete tax account');
    }
  };

  const updateCompanyField = (field, value) => {
    setCompanySettings(prev => ({ ...prev, [field]: value }));
  };

  const updateAddressField = (type, field, value) => {
    setCompanySettings(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  const formatEIN = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) return cleaned;
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 9)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Tax Settings</h1>
          <p className="text-gray-500 mt-1">Configure federal, state, and local tax accounts</p>
        </div>
        <Button 
          onClick={handleSaveCompanySettings}
          disabled={saving}
          className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" /> Save Changes
            </>
          )}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="company">Company Info</TabsTrigger>
          <TabsTrigger value="federal">Federal Taxes</TabsTrigger>
          <TabsTrigger value="state">State Taxes</TabsTrigger>
          <TabsTrigger value="local">Local Taxes</TabsTrigger>
        </TabsList>

        {/* Company Information Tab */}
        <TabsContent value="company" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-600" />
                Company Identification
              </CardTitle>
              <CardDescription>Legal business information for tax purposes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="ein">Employer Identification Number (EIN) *</Label>
                  <Input
                    id="ein"
                    placeholder="XX-XXXXXXX"
                    value={companySettings.ein}
                    onChange={(e) => updateCompanyField('ein', formatEIN(e.target.value))}
                    maxLength={10}
                  />
                  <p className="text-xs text-gray-500">Federal Tax ID assigned by the IRS</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="entityType">Entity Type</Label>
                  <Select 
                    value={companySettings.entityType} 
                    onValueChange={(v) => updateCompanyField('entityType', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole_prop">Sole Proprietorship</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="llc">LLC</SelectItem>
                      <SelectItem value="s_corp">S Corporation</SelectItem>
                      <SelectItem value="c_corp">C Corporation</SelectItem>
                      <SelectItem value="nonprofit">Non-Profit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="legalName">Legal Business Name *</Label>
                  <Input
                    id="legalName"
                    placeholder="Legal name as registered"
                    value={companySettings.legalName}
                    onChange={(e) => updateCompanyField('legalName', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dba">DBA (Doing Business As)</Label>
                  <Input
                    id="dba"
                    placeholder="Trade name (if different)"
                    value={companySettings.dba}
                    onChange={(e) => updateCompanyField('dba', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Business Phone</Label>
                  <Input
                    id="phone"
                    placeholder="(555) 555-5555"
                    value={companySettings.phone}
                    onChange={(e) => updateCompanyField('phone', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Business Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="payroll@company.com"
                    value={companySettings.email}
                    onChange={(e) => updateCompanyField('email', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fiscalYearEnd">Fiscal Year End Month</Label>
                  <Select 
                    value={companySettings.fiscalYearEnd} 
                    onValueChange={(v) => updateCompanyField('fiscalYearEnd', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>
                          {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* Physical Address */}
              <div>
                <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4" /> Physical Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label>Street Address</Label>
                    <Input
                      placeholder="123 Main Street, Suite 100"
                      value={companySettings.address.street}
                      onChange={(e) => updateAddressField('address', 'street', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      placeholder="City"
                      value={companySettings.address.city}
                      onChange={(e) => updateAddressField('address', 'city', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select 
                      value={companySettings.address.state} 
                      onValueChange={(v) => updateAddressField('address', 'state', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(state => (
                          <SelectItem key={state.code} value={state.code}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>ZIP Code</Label>
                    <Input
                      placeholder="12345"
                      value={companySettings.address.zip}
                      onChange={(e) => updateAddressField('address', 'zip', e.target.value)}
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>

              {/* Mailing Address */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Mailing Address
                  </h3>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={companySettings.sameAsPhysical}
                      onCheckedChange={(v) => updateCompanyField('sameAsPhysical', v)}
                    />
                    <Label className="text-sm">Same as physical address</Label>
                  </div>
                </div>
                
                {!companySettings.sameAsPhysical && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2 space-y-2">
                      <Label>Street Address</Label>
                      <Input
                        placeholder="P.O. Box or Street Address"
                        value={companySettings.mailingAddress.street}
                        onChange={(e) => updateAddressField('mailingAddress', 'street', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        placeholder="City"
                        value={companySettings.mailingAddress.city}
                        onChange={(e) => updateAddressField('mailingAddress', 'city', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select 
                        value={companySettings.mailingAddress.state} 
                        onValueChange={(v) => updateAddressField('mailingAddress', 'state', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map(state => (
                            <SelectItem key={state.code} value={state.code}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>ZIP Code</Label>
                      <Input
                        placeholder="12345"
                        value={companySettings.mailingAddress.zip}
                        onChange={(e) => updateAddressField('mailingAddress', 'zip', e.target.value)}
                        maxLength={10}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Federal Taxes Tab */}
        <TabsContent value="federal" className="space-y-6">
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Federal tax deposits are due based on your deposit schedule. Most employers deposit semi-weekly if they had more than $50,000 in tax liability in the lookback period.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  Federal Tax Accounts
                </CardTitle>
                <CardDescription>IRS withholding, FICA, and FUTA accounts</CardDescription>
              </div>
              <Button 
                onClick={() => {
                  setNewTaxAccount({ ...newTaxAccount, jurisdiction: 'federal' });
                  setShowTaxAccountModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Account
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tax Type</TableHead>
                    <TableHead>Account Number</TableHead>
                    <TableHead>Filing Frequency</TableHead>
                    <TableHead>Deposit Schedule</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxAccounts.filter(a => a.jurisdiction === 'federal').length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No federal tax accounts configured. Add your IRS EFTPS account to enable tax deposits.
                      </TableCell>
                    </TableRow>
                  ) : (
                    taxAccounts.filter(a => a.jurisdiction === 'federal').map(account => (
                      <TableRow key={account.id}>
                        <TableCell className="font-medium">
                          {account.taxType === 'withholding' && 'Federal Withholding (941)'}
                          {account.taxType === 'futa' && 'FUTA (940)'}
                          {account.taxType === 'fica' && 'FICA (SS/Medicare)'}
                        </TableCell>
                        <TableCell>{account.accountNumber || 'Not set'}</TableCell>
                        <TableCell className="capitalize">{account.filingFrequency}</TableCell>
                        <TableCell className="capitalize">{account.depositSchedule}</TableCell>
                        <TableCell>
                          <Badge className={account.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                            {account.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setEditingTaxAccount(account)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTaxAccount(account.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Federal Tax Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Social Security</h4>
                    <p className="text-sm text-gray-500">6.2% employee / 6.2% employer</p>
                    <p className="text-xs text-gray-400 mt-1">2024 wage base: $168,600</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Medicare</h4>
                    <p className="text-sm text-gray-500">1.45% employee / 1.45% employer</p>
                    <p className="text-xs text-gray-400 mt-1">+0.9% additional over $200K</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">FUTA</h4>
                    <p className="text-sm text-gray-500">0.6% employer only</p>
                    <p className="text-xs text-gray-400 mt-1">Wage base: $7,000</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* State Taxes Tab */}
        <TabsContent value="state" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-600" />
                  State Tax Accounts
                </CardTitle>
                <CardDescription>State withholding and unemployment accounts</CardDescription>
              </div>
              <Button 
                onClick={() => {
                  setNewTaxAccount({ ...newTaxAccount, jurisdiction: 'state' });
                  setShowTaxAccountModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add State Account
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>State</TableHead>
                    <TableHead>Tax Type</TableHead>
                    <TableHead>Account Number</TableHead>
                    <TableHead>SUI Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxAccounts.filter(a => a.jurisdiction === 'state').length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No state tax accounts configured. Add accounts for states where you have employees.
                      </TableCell>
                    </TableRow>
                  ) : (
                    taxAccounts.filter(a => a.jurisdiction === 'state').map(account => {
                      const stateInfo = STATE_TAX_DATA[account.state];
                      const suiInfo = SUI_RATES[account.state];
                      return (
                        <TableRow key={account.id}>
                          <TableCell className="font-medium">
                            {stateInfo?.name || account.state}
                          </TableCell>
                          <TableCell>
                            {account.taxType === 'withholding' && 'State Withholding'}
                            {account.taxType === 'sui' && 'State Unemployment (SUI)'}
                            {account.taxType === 'sdi' && 'State Disability (SDI)'}
                            {account.taxType === 'pfml' && 'Paid Family Leave'}
                          </TableCell>
                          <TableCell>{account.accountNumber || 'Not set'}</TableCell>
                          <TableCell>
                            {account.taxType === 'sui' && suiInfo 
                              ? `${(account.suiRate || suiInfo.newEmployerRate) * 100}%` 
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={account.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                              {account.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => setEditingTaxAccount(account)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteTaxAccount(account.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* States without income tax */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>No state income tax:</strong> Alaska, Florida, Nevada, New Hampshire, South Dakota, Tennessee, Texas, Washington, Wyoming. 
              You may still need SUI accounts in these states.
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* Local Taxes Tab */}
        <TabsContent value="local" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-orange-600" />
                  Local Tax Jurisdictions
                </CardTitle>
                <CardDescription>City, county, and municipal tax accounts</CardDescription>
              </div>
              <Button 
                onClick={() => {
                  setNewTaxAccount({ ...newTaxAccount, jurisdiction: 'local' });
                  setShowTaxAccountModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Local Account
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Locality</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Tax Type</TableHead>
                    <TableHead>Account Number</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxAccounts.filter(a => a.jurisdiction === 'local').length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No local tax accounts configured. Add accounts if you have employees in cities with local income taxes (e.g., NYC, Philadelphia, Cleveland).
                      </TableCell>
                    </TableRow>
                  ) : (
                    taxAccounts.filter(a => a.jurisdiction === 'local').map(account => (
                      <TableRow key={account.id}>
                        <TableCell className="font-medium">{account.locality}</TableCell>
                        <TableCell>{account.state}</TableCell>
                        <TableCell>{account.taxType}</TableCell>
                        <TableCell>{account.accountNumber || 'Not set'}</TableCell>
                        <TableCell>{account.rate ? `${account.rate}%` : '-'}</TableCell>
                        <TableCell>
                          <Badge className={account.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                            {account.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setEditingTaxAccount(account)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTaxAccount(account.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Common local taxes:</strong> New York City, Yonkers, Philadelphia, Pittsburgh, Cleveland, Columbus, Cincinnati, Detroit, St. Louis, Kansas City, and many Ohio municipalities.
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Tax Account Modal */}
      <Dialog open={showTaxAccountModal || !!editingTaxAccount} onOpenChange={(open) => {
        if (!open) {
          setShowTaxAccountModal(false);
          setEditingTaxAccount(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTaxAccount ? 'Edit Tax Account' : 'Add Tax Account'}</DialogTitle>
            <DialogDescription>
              Configure tax account details for {editingTaxAccount?.jurisdiction || newTaxAccount.jurisdiction} taxes
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {(editingTaxAccount?.jurisdiction || newTaxAccount.jurisdiction) !== 'federal' && (
              <div className="space-y-2">
                <Label>State</Label>
                <Select
                  value={editingTaxAccount?.state || newTaxAccount.state}
                  onValueChange={(v) => editingTaxAccount 
                    ? setEditingTaxAccount({...editingTaxAccount, state: v})
                    : setNewTaxAccount({...newTaxAccount, state: v})
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(editingTaxAccount?.jurisdiction || newTaxAccount.jurisdiction) === 'local' && (
              <div className="space-y-2">
                <Label>Locality Name</Label>
                <Input
                  placeholder="City or county name"
                  value={editingTaxAccount?.locality || newTaxAccount.locality || ''}
                  onChange={(e) => editingTaxAccount 
                    ? setEditingTaxAccount({...editingTaxAccount, locality: e.target.value})
                    : setNewTaxAccount({...newTaxAccount, locality: e.target.value})
                  }
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Tax Type</Label>
              <Select
                value={editingTaxAccount?.taxType || newTaxAccount.taxType}
                onValueChange={(v) => editingTaxAccount 
                  ? setEditingTaxAccount({...editingTaxAccount, taxType: v})
                  : setNewTaxAccount({...newTaxAccount, taxType: v})
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(editingTaxAccount?.jurisdiction || newTaxAccount.jurisdiction) === 'federal' && (
                    <>
                      <SelectItem value="withholding">Federal Withholding (941)</SelectItem>
                      <SelectItem value="futa">FUTA (940)</SelectItem>
                    </>
                  )}
                  {(editingTaxAccount?.jurisdiction || newTaxAccount.jurisdiction) === 'state' && (
                    <>
                      <SelectItem value="withholding">State Withholding</SelectItem>
                      <SelectItem value="sui">State Unemployment (SUI)</SelectItem>
                      <SelectItem value="sdi">State Disability (SDI)</SelectItem>
                      <SelectItem value="pfml">Paid Family Leave</SelectItem>
                    </>
                  )}
                  {(editingTaxAccount?.jurisdiction || newTaxAccount.jurisdiction) === 'local' && (
                    <>
                      <SelectItem value="income">Local Income Tax</SelectItem>
                      <SelectItem value="occupational">Occupational Tax</SelectItem>
                      <SelectItem value="school">School District Tax</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Account Number / ID</Label>
              <Input
                placeholder="Tax account number"
                value={editingTaxAccount?.accountNumber || newTaxAccount.accountNumber}
                onChange={(e) => editingTaxAccount 
                  ? setEditingTaxAccount({...editingTaxAccount, accountNumber: e.target.value})
                  : setNewTaxAccount({...newTaxAccount, accountNumber: e.target.value})
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Filing Frequency</Label>
              <Select
                value={editingTaxAccount?.filingFrequency || newTaxAccount.filingFrequency}
                onValueChange={(v) => editingTaxAccount 
                  ? setEditingTaxAccount({...editingTaxAccount, filingFrequency: v})
                  : setNewTaxAccount({...newTaxAccount, filingFrequency: v})
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_FILING_FREQUENCIES.map(freq => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {((editingTaxAccount?.taxType || newTaxAccount.taxType) === 'sui') && (
              <div className="space-y-2">
                <Label>SUI Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 2.7"
                  value={editingTaxAccount?.suiRate || newTaxAccount.suiRate || ''}
                  onChange={(e) => editingTaxAccount 
                    ? setEditingTaxAccount({...editingTaxAccount, suiRate: parseFloat(e.target.value)})
                    : setNewTaxAccount({...newTaxAccount, suiRate: parseFloat(e.target.value)})
                  }
                />
              </div>
            )}

            {((editingTaxAccount?.jurisdiction || newTaxAccount.jurisdiction) === 'local') && (
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 3.0"
                  value={editingTaxAccount?.rate || newTaxAccount.rate || ''}
                  onChange={(e) => editingTaxAccount 
                    ? setEditingTaxAccount({...editingTaxAccount, rate: parseFloat(e.target.value)})
                    : setNewTaxAccount({...newTaxAccount, rate: parseFloat(e.target.value)})
                  }
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowTaxAccountModal(false);
              setEditingTaxAccount(null);
            }}>
              Cancel
            </Button>
            <Button onClick={editingTaxAccount ? handleUpdateTaxAccount : handleAddTaxAccount}>
              {editingTaxAccount ? 'Update' : 'Add'} Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
